/**
 * The kill cam (Phase 8): when the own mech is destroyed, the last moments replay from the
 * winner's shoulder during the round-end pause, at 0.8x, from poses the client already drew.
 * Nothing new crosses the wire and nothing here has authority; a pose history and a little
 * arithmetic. Any key skips it; the next round start ends it.
 */
import type { Slot } from '@steelwake/shared/combat';

export interface PoseSample {
  x: number;
  y: number;
  z: number;
  yaw: number;
  aimYaw: number;
  aimPitch: number;
}

export interface HistoryFrame {
  t: number;
  poses: [PoseSample, PoseSample];
}

/** The last few seconds of drawn poses, one frame per render. */
export class PoseHistory {
  private frames: HistoryFrame[] = [];

  constructor(readonly seconds = 4) {}

  push(frame: HistoryFrame): void {
    this.frames.push(frame);
    const cutoff = frame.t - this.seconds;
    let drop = 0;
    while (drop < this.frames.length - 1 && (this.frames[drop + 1]?.t ?? Infinity) <= cutoff) {
      drop++;
    }
    if (drop > 0) this.frames.splice(0, drop);
  }

  /** The newest frame's time, or null when empty. */
  latest(): number | null {
    return this.frames.at(-1)?.t ?? null;
  }

  /** Poses at time `t`, interpolated between the frames around it (clamped to the ends). */
  at(t: number): [PoseSample, PoseSample] | null {
    const first = this.frames[0];
    const last = this.frames.at(-1);
    if (!first || !last) return null;
    if (t <= first.t) return first.poses;
    if (t >= last.t) return last.poses;
    let lo = 0;
    let hi = this.frames.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if ((this.frames[mid]?.t ?? 0) <= t) lo = mid;
      else hi = mid;
    }
    const a = this.frames[lo];
    const b = this.frames[hi];
    if (!a || !b) return last.poses;
    const span = b.t - a.t;
    const k = span > 0 ? (t - a.t) / span : 0;
    return [lerpPose(a.poses[0], b.poses[0], k), lerpPose(a.poses[1], b.poses[1], k)];
  }

  clear(): void {
    this.frames = [];
  }

  get size(): number {
    return this.frames.length;
  }
}

function lerpAngle(a: number, b: number, k: number): number {
  const d = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + d * k;
}

function lerpPose(a: PoseSample, b: PoseSample, k: number): PoseSample {
  return {
    x: a.x + (b.x - a.x) * k,
    y: a.y + (b.y - a.y) * k,
    z: a.z + (b.z - a.z) * k,
    yaw: lerpAngle(a.yaw, b.yaw, k),
    aimYaw: lerpAngle(a.aimYaw, b.aimYaw, k),
    aimPitch: a.aimPitch + (b.aimPitch - a.aimPitch) * k,
  };
}

export interface KillCam {
  killer: Slot;
  /** Wall-clock (render time) when the replay started and when it ends. */
  start: number;
  until: number;
  /** History time the replay begins from; it advances at `speed`. */
  replayFrom: number;
  speed: number;
}

export const KILL_CAM_SECONDS = 3;
export const KILL_CAM_SPEED = 0.8;

/**
 * A replay of the last `KILL_CAM_SECONDS` (at 0.8x) that fits inside the round-end pause with a
 * second to spare; null when the pause is too short to show anything.
 */
export function startKillCam(
  now: number,
  killer: Slot,
  historyLatest: number,
  roundEndSeconds: number,
): KillCam | null {
  const length = Math.min(KILL_CAM_SECONDS, roundEndSeconds - 1);
  if (length < 0.5) return null;
  return {
    killer,
    start: now,
    until: now + length,
    replayFrom: historyLatest - length * KILL_CAM_SPEED,
    speed: KILL_CAM_SPEED,
  };
}

export function replayTime(cam: KillCam, now: number): number {
  return cam.replayFrom + (now - cam.start) * cam.speed;
}

export function killCamOver(cam: KillCam, now: number): boolean {
  return now >= cam.until;
}
